import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams  } from "react-router-dom";
import axios from "axios";
import useFormSchema from "../formModels/useFormSchema";
import SimpleTextField from "./SimpleTextField";
import LanguageValueField from "./LanguageValueField";
import AdmsIdentifierList from "../formModels/AdmsIdentifierList";
import AdmsIdentifier from "../formModels/AdmsIdentifier";
import LanguageValueList from "../formModels/LanguageValueList";
import GenericLangValueList from "../formModels/GenericLangValueList";
import Address from "../formModels/Address";
import Person from "../formModels/Person";
import PersonSingle from "./PersonSingle";
import AddressView from "./AddressView";
import TextFieldList from "./TextFieldList";
import AdmsIdentifierListView from "./AdmsIdentifierListView";
import AutocompleteChipsView from "./AutocompleteChipsView";
import {getTitleByField, getDescriptionByField, parseValidationErrors, cleanEmptyFields, replaceIdOnlyObjectsWithNull, validateFormData} from "../config/commonFunctions"; // validateFormData
import { Stack, Button, Typography, Paper, Checkbox, FormControlLabel, CircularProgress } from "@mui/material";
import ErrorIcon from '@mui/icons-material/Error';
import AutocompleteSingle from "./AutocompleteSingle";
import { PARTICIPANT_FORM_SUBMITION, PARTICIPANT_FORM_SCHEMA } from "../config/settings";
// import termsPdf from '../assets/LDS_terms_of_service.pdf';

const initialFormData = {
  "address": new Address(),
  "other_address": new Address(),
  "legal_representative": new Person(),
  "technical_contact": new Person(),
  "identifier": new AdmsIdentifierList(),
  "name": new LanguageValueList(),
  "short_name": new LanguageValueList(),
  "activities": new GenericLangValueList(),
  "description": new LanguageValueList(),
  "vat": "",
  "organization_type": "",
  "company_type": "",
  "location_of_incorporation": "",
  "contact_website": [],
  "eu_id": "",
};
const ENDPOINT_URL = PARTICIPANT_FORM_SUBMITION;

const ParticipantForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editingParticipant = location.state?.participant || null;
  // console.log(editingParticipant);
  const [isEditMode, setIsEditMode] = useState(false); // To track edit mode
  const [participantId, setParticipantId] = useState(null); // For the PUT request
  const [globalLanguage, setGlobalLanguage] = useState("en"); // State for global language
  const [schemaAsObject, setSchemaAsObject] = useState({});
  const [dataToSave, setDataToSave] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [companyTypeOptions, setCompanyTypeOptions] = useState([]);
  const [country, setCountry] = useState([]);
  const [hasAcknowledgedTerms, setHasAcknowledgedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [formErrors, setFormErrors] = useState({allErrors:{}, addressErrors:{}, personErrors:{}});
  // console.log(schemaAsObject);
  const isFieldRequired = (field) => {
    // Check if required array exists in the schema
    return schemaAsObject?.required?.includes(field) ?? false;
  };

  const {
    formData,
    handleChange,
    handleIdentifierChange,
    handleAddressChange,
    handlePersonChange,
    handleFieldChange,
    // handleLogoUpload,
  } = useFormSchema(initialFormData);

  /* useEffect(() => {
    serializeFormData();
  }, [formData]); */

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true); // Set loading state
        const url = PARTICIPANT_FORM_SCHEMA + globalLanguage + "/";
        // console.log("Fetching URL:", url);
        const result = await axios.get(url);
        setSchemaAsObject(result.data);
        // console.log("Fetched Schema:", result.data);
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching schema:", err);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [globalLanguage]);

  useEffect(() => {
    if (schemaAsObject?.$defs?.CompanyType) {
      setCompanyTypeOptions(schemaAsObject.$defs.CompanyType.vocab); // Ensure $defs and CompanyType exist
    }
    if (schemaAsObject?.$defs?.Country) {
      setCountry(schemaAsObject.$defs.Country.vocab); // Ensure $defs and CompanyType exist
    }
  }, [schemaAsObject]);
  // console.log(schemaAsObject);

  // Check if we're in edit mode by looking for participant data in location.state
  useEffect(() => {
    if (location.state?.participant) {
      setIsEditMode(true);
      setHasAcknowledgedTerms(true);
      setParticipantId(location.state.participant.id); // Store the participant ID for the PUT request
      // console.log("editingParticipant", editingParticipant); //location.state.participant
      // Prefill formData with the participant's data
      const prepopulateFormData = {
        name: new LanguageValueList(editingParticipant.name),
        short_name: new LanguageValueList(editingParticipant.short_name),
        activities: new GenericLangValueList(editingParticipant.activities),
        description: new LanguageValueList(editingParticipant.description),
        vat: editingParticipant.vat,
        organization_type: editingParticipant.organization_type,
        company_type: editingParticipant.company_type,
        location_of_incorporation: editingParticipant.location_of_incorporation,
        contact_website: editingParticipant.contact_website,
        eu_id: editingParticipant.eu_id,
        address: new Address(new LanguageValueList(editingParticipant.address.street_address),
          new LanguageValueList(editingParticipant.address.administrative_area),
          new LanguageValueList(editingParticipant.address.city),
          editingParticipant.address.postal_code,
          editingParticipant.address.country,
          editingParticipant.address.id),
        other_address: editingParticipant.other_address ? new Address(new LanguageValueList(editingParticipant.other_address.street_address),
          new LanguageValueList(editingParticipant.other_address.administrative_area),
          new LanguageValueList(editingParticipant.other_address.city),
          editingParticipant.other_address.postal_code,
          editingParticipant.other_address.country,
          editingParticipant.other_address.id) : new Address(),
        identifier: (Array.isArray(editingParticipant.identifier) && editingParticipant.identifier.length > 0)
          ? new AdmsIdentifierList(
            editingParticipant.identifier.map(
              item => new AdmsIdentifier(item.identifier_notation, new LanguageValueList(item.creator), item.id)
            )
          )
          : new AdmsIdentifierList(),
        legal_representative: new Person(new LanguageValueList(editingParticipant.legal_representative.name), editingParticipant.legal_representative.email, editingParticipant.legal_representative.id),
        technical_contact: editingParticipant.technical_contact ? new Person(new LanguageValueList(editingParticipant.technical_contact.name), editingParticipant.technical_contact.email, editingParticipant.technical_contact.id) : new Person(),
        // logo: editingParticipant.logo,
        // connector: editingParticipant.connector,
      };
      // console.log("prepopulateFormData", prepopulateFormData);
      // Update the formData in useFormSchema hook with the pre-populated data
      handleFieldChange("name", prepopulateFormData.name);
      handleFieldChange("short_name", prepopulateFormData.short_name);
      handleFieldChange("activities", prepopulateFormData.activities);
      handleFieldChange("description", prepopulateFormData.description);
      handleChange("vat", prepopulateFormData.vat);
      handleChange("organization_type", prepopulateFormData.organization_type);
      handleChange("company_type", prepopulateFormData.company_type);
      handleChange("location_of_incorporation", prepopulateFormData.location_of_incorporation);
      handleChange("contact_website", prepopulateFormData.contact_website);
      handleChange("eu_id", prepopulateFormData.eu_id);
      handleIdentifierChange(prepopulateFormData.identifier.getAllModels());
      handlePersonChange("legal_representative", prepopulateFormData.legal_representative);
      handleAddressChange("address", prepopulateFormData.address);
      handleAddressChange("other_address", prepopulateFormData.other_address);
      handlePersonChange("technical_contact", prepopulateFormData.technical_contact);
      // handleChange("logo", prepopulateFormData.logo);
      // handleChange("connector", prepopulateFormData.connector);
    }
  }, [editingParticipant]);


  // Function to serialize formData into a simple JSON object
  const serializeFormData = (currentFormData) => {
    const serializedData = {
      name: currentFormData.name.toJSON(), // Converts to array of language-value pairs
      short_name: currentFormData.short_name.toJSON(),
      activities: currentFormData.activities.toJSON(),
      description: currentFormData.description.toJSON(),
      vat: currentFormData.vat,
      organization_type: currentFormData.organization_type,
      company_type: currentFormData.company_type,
      location_of_incorporation: currentFormData.location_of_incorporation,
      contact_website: currentFormData.contact_website,
      eu_id: currentFormData.eu_id,
      address: currentFormData.address.toJSON(),
      other_address: currentFormData.other_address.toJSON(),
      identifier: currentFormData.identifier.toJSON(),
      legal_representative: currentFormData.legal_representative.toJSON(),
      technical_contact: currentFormData.technical_contact.toJSON(),
      // logo: currentFormData.logo,
      // connector: currentFormData.connector,
    };
    return serializedData;
    // setDataToSave(serializedData); // Update the dataToSave state
  };

  const handleAcknowledgeChange = (event) => {
    setHasAcknowledgedTerms(event.target.checked);
  };

  const handleSubmit = () => {
    setLoading(true);
    const finalToSave = serializeFormData(formData);
    if (isEditMode) {
      // PUT request for editing existing participant
      const cleanedDataStep1 = cleanEmptyFields(finalToSave);
      const cleanedData = replaceIdOnlyObjectsWithNull(cleanedDataStep1);
      // console.log("Data to save:", cleanedData);
      axios
        .put(`${ENDPOINT_URL}${participantId}/`, cleanedData)
        .then((response) => {
          // console.log("Participant updated", response);
          navigate("/management");
        })
        .catch((err) => {
          if (err.response && err.response.status === 400) {
            setValidationError(err.response.data);
          } else {
            console.error("Unexpected error occurred:", err);
          }
        })
        .finally(() => setLoading(false));
    } else {
      // POST request for creating a new participant
       const cleanedData = cleanEmptyFields(finalToSave);
       console.log("Data to save:", cleanedData);
       axios
        .post(ENDPOINT_URL, cleanedData)
        .then((response) => {
          // console.log("Participant created", response);
          navigate("/submission-success"); // Redirect to the submission success page
        })
        .catch((err) => {
          if (err.response && err.response.status === 400) {
            setValidationError(err.response.data);
          } else {
            console.error("Unexpected error occurred:", err);
          }
        })
        .finally(() => setLoading(false));
    }
  };

  function printError(errorMessages) {
    if (!errorMessages) {
      return <></>;
    }
    // console.log(errorMessages);
    const errors = parseValidationErrors(errorMessages);
    // Split the errors string into an array by newline characters
    const errorLines = errors.split("\n").filter((line) => line.trim() !== "");
    return (
      <Paper style={{ padding: "1%"}}>
        <div className="section--title--warning flex flex--center">
          <span><ErrorIcon className="nav-icon-xlarge" /></span>
          <span><Typography variant="h6" noWrap>Validation notices</Typography></span>
        </div>
        <div>
          {errorLines.map((error, index) => (
            <div key={index} style={{paddingBottom: "0.5em"}}>{error}</div> // Each error rendered in its own div
          ))}
        </div>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {!isEditMode ? (<div>
        <Typography variant="h3" sx={{ mb: 1, mt: 5 }}>
          Participant Registration Form
        </Typography>
        <Typography variant="h6" sx={{ mb: 3, mt: 1 }}>
          Join the Common European Language Data Space.
        </Typography>
      </div>) : (<div>
        <Typography variant="h3" sx={{ mb: 1, mt: 5 }}>
          Organization info
        </Typography>
      </div>)}
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <LanguageValueField
            id={"name-id"}
            label={getTitleByField(schemaAsObject, "name")}
            helperText={getDescriptionByField(schemaAsObject, "name")}
            obj={formData?.name}
            lang={globalLanguage}
            required={isFieldRequired("name")}
            onChange={(updatedValues) => handleFieldChange("name", updatedValues)}
            // error={formErrors.allErrors.name}
          />

          <SimpleTextField
            label={getTitleByField(schemaAsObject, "vat")}
            helperText={getDescriptionByField(schemaAsObject, "vat") + ". If you do not have a VAT number  please enter -"}
            value={formData.vat || ""}
            onChange={(value) => handleChange("vat", value)}
            required={isFieldRequired("vat")}
            // error={formErrors.allErrors.vat}
          />

          <SimpleTextField
            label={getTitleByField(schemaAsObject, "organization_type")}
            helperText={getDescriptionByField(schemaAsObject, "organization_type")}
            value={formData.organization_type || ""}
            onChange={(value) => handleChange("organization_type", value)}
            required={isFieldRequired("organization_type")}
            // error={formErrors.allErrors.organization_type}
          />

          <AutocompleteSingle
            title={getTitleByField(schemaAsObject, "company_type")}
            helperText={getDescriptionByField(schemaAsObject, "company_type")}
            options={companyTypeOptions}
            onChange={(value) => handleChange("company_type", value)} // Update with the value selected
            required={isFieldRequired("company_type")}
            value={
              formData.company_type
                ? companyTypeOptions.find(option => option.value === formData.company_type)
                : null // If no prepopulated data, set to null to display empty field
            }
            // error={formErrors.allErrors.company_type}
          />

          <AutocompleteSingle
            title={getTitleByField(schemaAsObject, "location_of_incorporation")}
            helperText={getDescriptionByField(schemaAsObject, "location_of_incorporation")}
            options={country}
            onChange={(value) => handleChange("location_of_incorporation", value)} // Update with the value selected
            required={isFieldRequired("location_of_incorporation")}
            value={
              formData.location_of_incorporation
                ? country.find(option => option.value === formData.location_of_incorporation)
                : null // If no prepopulated data, set to null to display empty field
            }
            // error={formErrors.allErrors.location_of_incorporation}
          />

          <TextFieldList
            id={"contact_website"}
            title={getTitleByField(schemaAsObject, "contact_website")}
            subtitle={getDescriptionByField(schemaAsObject, "contact_website")}
            values={formData?.contact_website} // Pass the websites state as values
            onChange={(values) => handleChange("contact_website", values)}
            required={isFieldRequired("contact_website")}
            // error={formErrors.allErrors.contact_website}
          />

          <AddressView address={formData.address}
            handleChange={(updatedAddress) => handleAddressChange("address", updatedAddress)}
            fieldName="address"
            title={getTitleByField(schemaAsObject, "address")}
            lang={globalLanguage}
            countryOptions={country}
            schemaAsObject={schemaAsObject}
            subtitle={getDescriptionByField(schemaAsObject, "address")}
            required={isFieldRequired("address")}
            // error={formErrors.addressErrors}
          />

        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <PersonSingle
          title={getTitleByField(schemaAsObject, "legal_representative")}
          subtitle={getDescriptionByField(schemaAsObject, "legal_representative")}
          id={"legal_representative"}
          initialPerson={formData.legal_representative}
          onChange={(updatedPerson) => handlePersonChange("legal_representative", updatedPerson)}
          lang={globalLanguage}
          schemaAsObject={schemaAsObject}
          required={isFieldRequired("legal_representative")}
          // error={formErrors.personErrors}
          requirementException={false}
          // validationError={validationError?.legal_representative}
        />
      </Paper>

      {<Paper sx={{ p: 2 }}>
        <PersonSingle
          title={getTitleByField(schemaAsObject, "technical_contact")}
          subtitle={getDescriptionByField(schemaAsObject, "technical_contact")}
          schemaAsObject={schemaAsObject}
          id={"technical_contact"}
          initialPerson={formData.technical_contact}
          onChange={(updatedPerson) => handlePersonChange("technical_contact", updatedPerson)}
          lang={globalLanguage}
          required={isFieldRequired("technical_contact")}
          // error={false}
          requirementException={true}
          // validationError={validationError?.technical_contact}
        />
      </Paper>}

      <Paper sx={{ p: 2 }}>
        <AddressView address={formData.other_address}
          handleChange={(updatedAddress) => handleAddressChange("other_address", updatedAddress)}
          fieldName="other_address"
          title={getTitleByField(schemaAsObject, "other_address")}
          lang={globalLanguage}
          countryOptions={country}
          schemaAsObject={schemaAsObject}
          subtitle={getDescriptionByField(schemaAsObject, "other_address")}
          required={isFieldRequired("other_address")}
          // error={false}
        />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <LanguageValueField
            id={"short_name-id"}
            label={getTitleByField(schemaAsObject, "short_name")}
            helperText={getDescriptionByField(schemaAsObject, "short_name")}
            obj={formData?.short_name}
            lang={globalLanguage}
            required={isFieldRequired("short_name")}
            onChange={(updatedValues) => handleFieldChange("short_name", updatedValues)}
            // error={formErrors.allErrors.short_name}
          />

          <AutocompleteChipsView
            id={"activities-id"}
            label={getTitleByField(schemaAsObject, "activities")}
            helperText={getDescriptionByField(schemaAsObject, "activities")}
            // placeholder={getPlaceholderFromProperty(schemaAsObject, "activities")}
            required={isFieldRequired("activities")}
            objList={formData?.activities}
            lang={globalLanguage}
            onChange={(updatedValues) => handleFieldChange("activities", updatedValues)}
          />

          <LanguageValueField
            id={"description-id"}
            label={getTitleByField(schemaAsObject, "description")}
            helperText={getDescriptionByField(schemaAsObject, "description")}
            multiline={true}
            minRows={3}
            obj={formData?.description}
            lang={globalLanguage}
            required={isFieldRequired("description")}
            onChange={(updatedValues) => handleFieldChange("description", updatedValues)}
            // error={formErrors.allErrors.description}
          />

          {/* <SimpleTextField
            label={getTitleByField(schemaAsObject, "logo") ? getTitleByField(schemaAsObject, "logo") : "logo"}
            helperText={getDescriptionByField(schemaAsObject, "logo") ? getDescriptionByField(schemaAsObject, "logo") : ""}
            value={formData.logo || ""}
            onChange={(value) => handleChange("logo", value)}
            required={isFieldRequired("logo")}
          /> */}

        </Stack>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <SimpleTextField
            label={getTitleByField(schemaAsObject, "eu_id") ? getTitleByField(schemaAsObject, "eu_id") : "eu_id"}
            helperText={getDescriptionByField(schemaAsObject, "eu_id") ? getDescriptionByField(schemaAsObject, "eu_id") : ""}
            value={formData.eu_id || ""}
            onChange={(value) => handleChange("eu_id", value)}
            required={isFieldRequired("eu_id")}
            // error={formErrors.allErrors.eu_id}
          />

          <AdmsIdentifierListView
            schemaAsObject={schemaAsObject}
            title={getTitleByField(schemaAsObject, "identifier")}
            subtitle={getDescriptionByField(schemaAsObject, "identifier")}
            obj={formData?.identifier.getAllModels()}
            lang={globalLanguage}
            onIdentifierChange={handleIdentifierChange}
            required={isFieldRequired("identifier")}
          />
        </Stack>
      </Paper>

      {validationError && <>
        <Stack sx={{ mt: 2 }}>{printError(validationError)}</Stack>
      </> }

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <div style={{ padding: "1em" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasAcknowledgedTerms}
                  onChange={handleAcknowledgeChange}
                  disabled={isEditMode}
                  name="acknowledgeTerms"
                />
              }
              label={
                <span>
                  I acknowledge that I have read, understood, and agree to be bound by the{" "}
                  <a
                    href="https://language-data-space.ec.europa.eu/general-terms-and-conditions-testing-language-data-space"
                    rel="external" target="_blank">
                    LDS General Terms and Conditions
                  </a>.
                </span>
              }
            />
          </div>
          <div style={{ padding: "1em", backgroundColor: "#f8fafc" }}><p>For any information regarding the processing of your personal data, please refer to the <a href="https://language-data-space.ec.europa.eu/data-processing-agreement" rel="external" target="_blank">Privacy Statement.</a></p></div>
        </Stack>
      </Paper>

      <Button onClick={handleSubmit} variant="contained" disabled={!hasAcknowledgedTerms} color="success"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
        {isEditMode ? "Update" : "Submit"}
      </Button>
    </Stack>
  );
};

export default ParticipantForm;
